import { type FC, type ReactNode } from "react";

import { DropdownInput, Option, TooltipIndicator, TooltipPlacementTypes } from "@snapchat/snap-design-system";

import { Flexbox } from "../../../shared/components";
import { type KeyedOption } from "../../../shared/models";

import {
    ChooseLensFolder,
    LensFolderTooltip,
    Organization,
    OrganizationTooltip,
    SelectLensFolder,
    SelectOrganization,
} from "./messages.generated";
import { sectionHeader, sectionHeaderMargin, sectionMargin } from "./shared.styles";

interface DropdownInputSection {
    isLoading?: boolean;
    isDisabled?: boolean;
    onChange: (value: string) => void;
    tooltipText: ReactNode;
    sectionHeaderText: ReactNode;
    options: KeyedOption[];
    inputPlaceholder?: ReactNode;
    dataTestId?: string;
    defaultOption?: KeyedOption;
    value?: KeyedOption;
}

const DropdownInputSection: FC<DropdownInputSection> = ({
    isLoading,
    onChange,
    tooltipText,
    sectionHeaderText,
    options,
    inputPlaceholder,
    dataTestId,
    defaultOption,
    value,
}) => {
    return (
        <div className={sectionMargin}>
            <Flexbox alignItems="center" className={sectionHeaderMargin}>
                <div className={sectionHeader}>{sectionHeaderText}</div>
                <TooltipIndicator content={tooltipText} placement={TooltipPlacementTypes.TOP} />
            </Flexbox>
            <DropdownInput
                defaultValue={defaultOption?.key}
                placeholder={inputPlaceholder}
                onChange={onChange}
                disabled={isLoading}
                loading={isLoading}
                data-testid={dataTestId}
                value={value?.key}
            >
                {options.map(({ key, value, label }) => {
                    return (
                        <Option key={key} value={value}>
                            {label}
                        </Option>
                    );
                })}
            </DropdownInput>
        </div>
    );
};

interface DropdownInputSectionContainer {
    isLoading?: boolean;
    isDisabled?: boolean;
    onChange: (value: string) => void;
    options: KeyedOption[];
    inputPlaceholder?: string;
    defaultOption?: KeyedOption;
    value?: KeyedOption;
}

export const SelectOrganizationDropdownInputSection: FC<DropdownInputSectionContainer> = ({
    isLoading,
    isDisabled,
    options,
    onChange,
    defaultOption,
}) => {
    return (
        <DropdownInputSection
            isLoading={isLoading}
            isDisabled={isDisabled}
            options={options}
            onChange={onChange}
            tooltipText={<OrganizationTooltip />}
            sectionHeaderText={<Organization />}
            inputPlaceholder={<SelectOrganization />}
            defaultOption={defaultOption}
        />
    );
};

export const SelectLensSpaceDropdownInputSection: FC<DropdownInputSectionContainer> = ({
    isLoading,
    isDisabled,
    options,
    onChange,
    defaultOption,
    value,
}) => {
    return (
        <DropdownInputSection
            isLoading={isLoading}
            isDisabled={isDisabled}
            options={options}
            onChange={onChange}
            tooltipText={<LensFolderTooltip />}
            sectionHeaderText={<SelectLensFolder />}
            inputPlaceholder={<ChooseLensFolder />}
            dataTestId="select-lens-space"
            defaultOption={defaultOption}
            value={value}
        />
    );
};
